/**
 * FiveDImpactAnalyst — Ultra-detailed "Delay → Cost → Invoicing → Cashflow" panel
 * powered by Cognara (Cognara). Includes a chat interface for follow-up Q&A.
 * Data-source: /api/v1/projects/{id}/5d/ai-analyst/impact-simulation + /impact-chat
 */
import { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip as RTooltip,
  CartesianGrid, ReferenceLine, Line, ComposedChart, Bar,
} from 'recharts';
import {
  Sparkles, Loader2, AlertTriangle, DollarSign, FileText,
  TrendingDown, Zap, Send, MessageSquare, RefreshCw,
} from 'lucide-react';

const api = (p, opts = {}) => fetch(`${process.env.REACT_APP_BACKEND_URL}/api${p}`, opts);

const fmtM = (v) => {
  const n = Number(v || 0);
  if (Math.abs(n) >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (Math.abs(n) >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (Math.abs(n) >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
  return `$${n.toFixed(0)}`;
};

export default function FiveDImpactAnalyst({ projectId }) {
  const [sim, setSim] = useState(null);
  const [loading, setLoading] = useState(false);

  // chat state
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState('');
  const [asking, setAsking] = useState(false);

  // recovery-what-if state (Ask 3)
  const [recoveryDays, setRecoveryDays] = useState(0);
  const [seeding, setSeeding] = useState(false);

  const topDriver = sim?.evidence?.delayDrivers?.[0];

  const runSim = useCallback(async (overrides = {}) => {
    setLoading(true);
    try {
      const r = await api(`/v1/projects/${projectId}/5d/ai-analyst/impact-simulation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          recovery_task_id: overrides.recovery_task_id,
          recovery_days:    overrides.recovery_days ?? 0,
        }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || 'Failed');
      const d = await r.json();
      setSim(d);
      if (overrides.recovery_task_id) {
        toast.success(`Recovery applied: ${overrides.recovery_days} days`);
      } else {
        toast.success('Impact simulation ready');
      }
    } catch (e) {
      toast.error(e.message || 'Simulation failed');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  const applyRecovery = useCallback(() => {
    if (!topDriver || recoveryDays <= 0) return;
    runSim({ recovery_task_id: topDriver.taskId, recovery_days: recoveryDays });
  }, [runSim, topDriver, recoveryDays]);

  const seedDemo = useCallback(async () => {
    setSeeding(true);
    try {
      const r = await api(`/v1/projects/${projectId}/demo/seed-schedule`, { method: 'POST' });
      const d = await r.json();
      if (r.ok) {
        toast.success(`Seeded ${d.tasksSeeded} demo activities — re-run simulation`);
      } else {
        toast.error(d.detail || 'Seed failed');
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSeeding(false);
    }
  }, [projectId]);

  const ask = useCallback(async (q) => {
    const text = (q || '').trim();
    if (!text) return;
    const newHistory = [...msgs, { role: 'user', content: text }];
    setMsgs(newHistory);
    setInput('');
    setAsking(true);
    try {
      const r = await api(`/v1/projects/${projectId}/5d/ai-analyst/impact-chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question: text,
          history: msgs,
          evidence: sim?.evidence,
        }),
      });
      const d = await r.json();
      setMsgs([...newHistory, { role: 'assistant', content: d.answer || d.detail || '—' }]);
    } catch (e) {
      setMsgs([...newHistory, { role: 'assistant', content: `⚠️ ${e.message || 'Chat failed'}` }]);
    } finally {
      setAsking(false);
    }
  }, [msgs, projectId, sim]);

  return (
    <div className="h-full flex flex-col overflow-auto" data-testid="fived-impact-analyst"
      style={{ backgroundColor: 'var(--card-bg)', color: 'var(--text-primary)' }}>
      <div className="sticky top-0 z-10 flex items-center gap-2 px-4 py-2 backdrop-blur"
        style={{ borderBottom: '1px solid var(--border-color)', backgroundColor: 'var(--card-bg)' }}>
        <Sparkles className="w-4 h-4 text-purple-500" />
        <h2 className="text-sm font-semibold">Cognara — Delay Impact Simulation</h2>
        <span className="text-[10px] px-1.5 py-0.5 rounded font-mono"
          style={{ backgroundColor: 'rgba(139, 92, 246, 0.12)', color: '#8B5CF6' }}>
          Cognara · Ultra-Detail
        </span>
        <div className="ml-auto flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={seedDemo} disabled={seeding}
            className="h-7 text-xs" style={{ color: 'var(--text-muted)' }}
            data-testid="impact-seed-demo">
            {seeding ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
            Seed Al Dhafra Demo
          </Button>
          <Button size="sm" onClick={runSim} disabled={loading}
            className="h-7 text-xs bg-purple-600 hover:bg-purple-700 text-white"
            data-testid="impact-run-btn">
            {loading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
            {sim ? 'Re-Run' : 'Run Simulation'}
          </Button>
        </div>
      </div>

      {!sim && !loading && (
        <div className="flex-1 flex items-center justify-center text-center px-8">
          <div>
            <Sparkles className="w-10 h-10 mx-auto mb-3 text-purple-500" />
            <h3 className="text-base font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>Run Cognara to see delay impact</h3>
            <p className="text-xs max-w-md mx-auto" style={{ color: 'var(--text-muted)' }}>
              Cognara analyses the linked macro schedule, computes cost impact per delay driver
              (LD + prolongation + escalation + rework), invoicing slippage, and re-forecasts
              your weekly cashflow. Click <b>Run Simulation</b> to begin.
            </p>
          </div>
        </div>
      )}

      {loading && (
        <div className="flex-1 flex items-center justify-center text-sm" style={{ color: 'var(--text-muted)' }}>
          <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Cognara is analysing the schedule…
        </div>
      )}

      {sim && (
        <div className="p-4 space-y-4">
          {/* Headline */}
          {sim.headline && (
            <div className="rounded-lg p-3 border-l-4 border-purple-500 bg-purple-500/5" data-testid="impact-headline">
              <div className="text-[10px] uppercase tracking-wider text-purple-300 mb-1">Cognara Headline</div>
              <div className="text-sm">{sim.headline}</div>
            </div>
          )}

          {/* KPI strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="impact-kpis">
            <KpiCard label="Delay Drivers"       value={sim.evidence?.delayDrivers?.length || 0}     icon={<AlertTriangle className="w-3 h-3 text-red-400" />} />
            <KpiCard label="Worst Delay"         value={`${sim.evidence?.worstDelayWeeks || 0} wks`}  icon={<TrendingDown className="w-3 h-3 text-amber-400" />} />
            <KpiCard label="Total Cost Impact"   value={fmtM(sim.evidence?.costImpact?.total)}       icon={<DollarSign className="w-3 h-3 text-red-400" />} />
            <KpiCard label="Revenue at Risk"     value={fmtM(sim.evidence?.invoicingImpact?.revenueAtRisk)} icon={<FileText className="w-3 h-3 text-amber-400" />} />
          </div>

          {/* 5 Sections in a grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="impact-sections">
            {sim.sections?.map((sec, i) => (
              <SectionCard key={sec.id || i} sec={sec} evidence={sim.evidence} />
            ))}
          </div>

          {/* Cashflow chart */}
          {sim.evidence?.cashFlowWeekly && sim.evidence.cashFlowWeekly.length > 0 && (
            <div className="rounded-lg p-3 border border-slate-800 bg-slate-900/40" data-testid="impact-cashflow-chart">
              <div className="flex items-center gap-2 mb-2">
                <Zap className="w-3.5 h-3.5 text-amber-400" />
                <div className="text-[11px] uppercase tracking-wider font-semibold">Cashflow Impact — Baseline vs Delay-Shifted (12 wks)</div>
              </div>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={sim.evidence.cashFlowWeekly} margin={{ top: 10, right: 20, bottom: 10, left: 8 }}>
                    <CartesianGrid stroke="#334155" strokeDasharray="3 3" />
                    <XAxis dataKey="week" tick={{ fill: '#94A3B8', fontSize: 10 }} />
                    <YAxis tick={{ fill: '#94A3B8', fontSize: 10 }} tickFormatter={fmtM} />
                    <RTooltip
                      contentStyle={{ background: '#0F172A', border: '1px solid #334155', borderRadius: 6, fontSize: 11 }}
                      formatter={(v) => fmtM(v)}
                    />
                    <Bar dataKey="gap" fill="#EF4444" fillOpacity={0.4} name="Gap ($)" />
                    <Line type="monotone" dataKey="cumBaseline"  stroke="#3B82F6" strokeWidth={2} dot={false} name="Baseline Cum" />
                    <Line type="monotone" dataKey="cumForecast"  stroke="#F59E0B" strokeWidth={2} dot={false} name="Shifted Cum" />
                    <ReferenceLine x={sim.evidence.cashFlowWeekly[Math.round(sim.evidence.worstDelayWeeks || 0)]?.week}
                                   stroke="#EF4444" strokeDasharray="4 4" label={{ value: 'Delay Shift', fill: '#EF4444', fontSize: 10, position: 'top' }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Delay Drivers table */}
          {sim.evidence?.delayDrivers && sim.evidence.delayDrivers.length > 0 && (
            <div className="rounded-lg border border-slate-800 bg-slate-900/40 overflow-hidden" data-testid="impact-drivers-table">
              <div className="px-3 py-2 border-b border-slate-800 flex items-center gap-2">
                <AlertTriangle className="w-3.5 h-3.5 text-red-400" />
                <div className="text-[11px] uppercase tracking-wider font-semibold">Top Delay Drivers (Cost Impact per Activity)</div>
              </div>
              <div className="overflow-auto max-h-72">
                <table className="w-full text-xs">
                  <thead className="bg-slate-800/50 sticky top-0">
                    <tr className="text-left text-[10px] uppercase tracking-wider text-slate-400">
                      <th className="px-2 py-1.5">Code</th>
                      <th className="px-2 py-1.5">Activity</th>
                      <th className="px-2 py-1.5 text-right">Delay</th>
                      <th className="px-2 py-1.5 text-right">BAC</th>
                      <th className="px-2 py-1.5 text-right">LD</th>
                      <th className="px-2 py-1.5 text-right">Prolong</th>
                      <th className="px-2 py-1.5 text-right">Esc</th>
                      <th className="px-2 py-1.5 text-right">Rework</th>
                      <th className="px-2 py-1.5 text-right">Total $</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sim.evidence.delayDrivers.map((d) => (
                      <tr key={d.taskId} className="border-t border-slate-800/60 hover:bg-slate-800/30">
                        <td className="px-2 py-1.5 font-mono text-[10px]">{d.code}</td>
                        <td className="px-2 py-1.5">
                          <span className={d.isCritical ? 'text-red-300' : ''}>{d.name}</span>
                          {d.isCritical && <span className="ml-1 text-[9px] px-1 rounded bg-red-500/20 text-red-300">CP</span>}
                        </td>
                        <td className="px-2 py-1.5 text-right text-amber-300">{d.delayDays}d</td>
                        <td className="px-2 py-1.5 text-right">{fmtM(d.budgetedCost)}</td>
                        <td className="px-2 py-1.5 text-right">{fmtM(d.costImpact?.liquidatedDamages)}</td>
                        <td className="px-2 py-1.5 text-right">{fmtM(d.costImpact?.prolongation)}</td>
                        <td className="px-2 py-1.5 text-right">{fmtM(d.costImpact?.escalation)}</td>
                        <td className="px-2 py-1.5 text-right">{fmtM(d.costImpact?.rework)}</td>
                        <td className="px-2 py-1.5 text-right font-semibold text-red-300">{fmtM(d.costImpact?.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Recovery Simulation Slider (Ask: Recovery Simulation) */}
          {topDriver && (
            <div className="rounded-lg p-3 border border-emerald-800 bg-emerald-500/5" data-testid="impact-recovery-panel">
              <div className="flex items-center gap-2 mb-2">
                <Zap className="w-3.5 h-3.5 text-emerald-400" />
                <div className="text-[11px] uppercase tracking-wider font-semibold">
                  Recovery What-If — <span className="text-emerald-300">{topDriver.name}</span>
                </div>
                <span className="ml-auto text-[10px] font-mono text-slate-400">
                  Current delay: <b className="text-red-300">{topDriver.delayDays}d</b>
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-[10px] text-slate-400 w-24">Recover days:</span>
                <input
                  type="range"
                  min="0"
                  max={Math.max(1, topDriver.delayDays + 7)}
                  value={recoveryDays}
                  onChange={(e) => setRecoveryDays(Number(e.target.value))}
                  className="flex-1 accent-emerald-500"
                  data-testid="impact-recovery-slider"
                />
                <span className="text-sm font-bold text-emerald-300 w-14 text-right">{recoveryDays}d</span>
                <Button size="sm" onClick={applyRecovery} disabled={loading || recoveryDays <= 0}
                  className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" data-testid="impact-recovery-apply">
                  {loading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
                  Apply
                </Button>
              </div>
              <p className="text-[10px] text-slate-500 mt-1.5">
                Drag to simulate cost/cashflow rebound if this driver is recovered by N days. The chart and KPIs above will refresh.
              </p>
            </div>
          )}

          {/* Chat interface */}
          <div className="rounded-lg border border-slate-800 bg-slate-900/40" data-testid="impact-chat">
            <div className="px-3 py-2 border-b border-slate-800 flex items-center gap-2">
              <MessageSquare className="w-3.5 h-3.5 text-purple-400" />
              <div className="text-[11px] uppercase tracking-wider font-semibold">Ask Cognara — Follow-up</div>
              {msgs.length > 0 && (
                <button onClick={() => setMsgs([])} className="ml-auto text-[10px] underline text-slate-400" data-testid="impact-chat-clear">Clear</button>
              )}
            </div>
            <div className="p-3 space-y-2">
              {msgs.length === 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {[
                    'Which driver has the biggest cost impact?',
                    'How much revenue am I risking this quarter?',
                    'What if I recover 2 weeks on the critical path?',
                    'Recommend the top-3 recovery actions.',
                  ].map((s) => (
                    <button key={s} onClick={() => ask(s)} disabled={asking}
                      className="text-[10px] px-2 py-1 rounded-full border border-slate-700 text-slate-300 hover:bg-slate-800"
                      data-testid={`impact-suggest-${s.slice(0, 8)}`}>{s}</button>
                  ))}
                </div>
              )}
              {msgs.length > 0 && (
                <div className="space-y-2 max-h-56 overflow-auto" data-testid="impact-chat-msgs">
                  {msgs.map((m, i) => (
                    <div key={i}
                      className={`rounded-lg px-3 py-2 text-xs leading-relaxed border`}
                      style={{
                        background: m.role === 'user' ? 'rgba(107,92,231,0.15)' : 'rgba(15,23,42,0.6)',
                        borderColor: m.role === 'user' ? 'rgba(107,92,231,0.4)' : '#334155',
                      }}
                      data-testid={`impact-chat-${m.role}-${i}`}>
                      <div className="text-[9px] uppercase tracking-wider text-slate-500 mb-1">
                        {m.role === 'user' ? 'You' : 'Cognara'}
                      </div>
                      <div style={{ whiteSpace: 'pre-wrap' }} className="text-slate-200">{m.content}</div>
                    </div>
                  ))}
                  {asking && (
                    <div className="flex items-center gap-2 text-xs text-slate-400"><Loader2 className="w-3 h-3 animate-spin" /> Cognara is thinking…</div>
                  )}
                </div>
              )}
              <form onSubmit={(e) => { e.preventDefault(); ask(input); }} className="flex items-center gap-2 pt-2">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask about drivers, cost impact, cashflow…"
                  disabled={asking}
                  className="flex-1 h-8 px-3 rounded-md text-xs outline-none bg-slate-950 border border-slate-700 text-slate-100"
                  data-testid="impact-chat-input"
                />
                <Button type="submit" size="sm" disabled={asking || !input.trim()} className="h-8 px-3 text-xs bg-purple-600 hover:bg-purple-700" data-testid="impact-chat-send">
                  {asking ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                </Button>
              </form>
            </div>
          </div>

          <div className="text-[10px] text-slate-500 flex items-center gap-3 pt-1">
            <span>Model: <b>{sim.model ? 'Cognara' : 'Cognara'}</b></span>
            <span>Generated: {new Date(sim.generatedAt).toLocaleString()}</span>
            <span>BAC: {fmtM(sim.evidence?.project?.bac)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

const KpiCard = ({ label, value, icon }) => (
  <div className="rounded-lg p-2.5 border border-slate-800 bg-slate-900/40">
    <div className="flex items-center gap-1.5 mb-1">
      {icon}<span className="text-[10px] uppercase tracking-wider text-slate-400">{label}</span>
    </div>
    <div className="text-base font-bold">{value}</div>
  </div>
);

const SectionCard = ({ sec }) => {
  const iconFor = (id) => {
    switch (id) {
      case 'drivers':   return <AlertTriangle className="w-3.5 h-3.5 text-red-400" />;
      case 'cost':      return <DollarSign     className="w-3.5 h-3.5 text-red-400" />;
      case 'invoicing': return <FileText       className="w-3.5 h-3.5 text-amber-400" />;
      case 'cashflow':  return <TrendingDown   className="w-3.5 h-3.5 text-amber-400" />;
      case 'actions':   return <Zap            className="w-3.5 h-3.5 text-purple-400" />;
      default:          return <Sparkles       className="w-3.5 h-3.5 text-purple-400" />;
    }
  };
  return (
    <div className="rounded-lg p-3 border border-slate-800 bg-slate-900/40" data-testid={`impact-section-${sec.id}`}>
      <div className="flex items-center gap-2 mb-1.5">
        {iconFor(sec.id)}
        <div className="text-[11px] uppercase tracking-wider font-semibold">{sec.title}</div>
      </div>
      {sec.summary && <div className="text-xs text-slate-300 leading-relaxed mb-2">{sec.summary}</div>}
      {Array.isArray(sec.insights) && sec.insights.length > 0 && (
        <ul className="space-y-1 text-xs">
          {sec.insights.map((ins, k) => (
            <li key={k} className="flex items-start gap-1.5 leading-relaxed">
              <span className="mt-1 w-1 h-1 rounded-full shrink-0 bg-purple-400" />
              <span className="text-slate-300">{ins}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
